import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { logGlobalError } from '../utils/globalErrorLogger';
import {
  getRedirectUser,
  logout as logoutWithProvider,
  resetPassword as resetPasswordWithProvider,
  signInWithEmail,
  signInWithGoogle as signInWithGoogleProvider,
  signUpWithEmail,
  subscribeToAuthState,
  type AuthUser,
  type GoogleSignInMode,
} from '../platform/firebase/auth';
import { createIdentityRepository } from '../platform/firebase/identityRepository';
import type { IdentityRepository } from '../domain/identity/repository';

export type { GoogleSignInMode } from '../platform/firebase/auth';

interface AuthContextType {
  currentUser: AuthUser | null;
  loading: boolean;
  isAdmin: boolean;
  isReviewer: boolean;
  reviewCampaignIds: string[];
  trustedCampaignIds?: string[];
  expertId: string | null;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (mode?: GoogleSignInMode) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectLoading, setRedirectLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReviewer, setIsReviewer] = useState(false);
  const [reviewCampaignIds, setReviewCampaignIds] = useState<string[]>([]);
  const [trustedCampaignIds, setTrustedCampaignIds] = useState<string[]>([]);
  const [expertId, setExpertId] = useState<string | null>(null);
  const initSeqRef = useRef(0);
  const identityRepositoryRef = useRef<IdentityRepository | null>(null);
  if (!identityRepositoryRef.current) {
    identityRepositoryRef.current = createIdentityRepository();
  }
  const identityRepository = identityRepositoryRef.current as IdentityRepository;

  async function signUp(email: string, password: string, displayName: string) {
    await signUpWithEmail(email, password, displayName);
    // The user doc is created by `bootstrapUser` in the auth-state handler.
  }

  async function signIn(email: string, password: string) {
    await signInWithEmail(email, password);
  }

  async function signInWithGoogle(mode: GoogleSignInMode = 'popup') {
    await signInWithGoogleProvider(mode);
  }

  async function logout() {
    await logoutWithProvider();
  }

  async function resetPassword(email: string) {
    await resetPasswordWithProvider(email);
  }

  useEffect(() => {
    let mounted = true;
    let roleUnsub: (() => void) | null = null;

    // Redirect-based sign-in returns through a fresh page load. Keep auth in a
    // loading state until Firebase has finished resolving any pending result so
    // the app does not flash back to the login screen mid-handoff.
    void getRedirectUser()
      .then((user) => {
        if (mounted && user) {
          setLoading(true);
          setCurrentUser(user);
        }
      })
      .catch((err) => {
        logGlobalError(err, { context: 'AuthContext.getRedirectResult' });
      })
      .finally(() => {
        if (mounted) {
          setRedirectLoading(false);
        }
      });

    const unsubscribe = subscribeToAuthState((user) => {
      const seq = initSeqRef.current + 1;
      initSeqRef.current = seq;

      if (roleUnsub) {
        roleUnsub();
        roleUnsub = null;
      }

      const init = async () => {
        setLoading(true);
        setCurrentUser(user);

        if (!user) {
          if (mounted && initSeqRef.current === seq) {
            setIsAdmin(false);
            setIsReviewer(false);
            setReviewCampaignIds([]);
            setTrustedCampaignIds([]);
            setExpertId(null);
            setLoading(false);
          }
          return;
        }

        try {
          const roles = await identityRepository.bootstrapUser(user);
          if (mounted && initSeqRef.current === seq) {
            setIsAdmin(roles.isAdmin);
            setIsReviewer(roles.isReviewer);
            setReviewCampaignIds(roles.reviewCampaignIds);
            setTrustedCampaignIds(roles.trustedCampaignIds ?? []);
            setExpertId(roles.expertId);
          }
        } catch (err) {
          logGlobalError(err, {
            context: 'AuthContext.bootstrapUser',
            metadata: { uid: user.uid },
          });
          if (mounted && initSeqRef.current === seq) {
            setIsAdmin(false);
            setIsReviewer(false);
            setReviewCampaignIds([]);
            setTrustedCampaignIds([]);
            setExpertId(null);
          }
        } finally {
          if (mounted && initSeqRef.current === seq) {
            setLoading(false);
          }
        }

        // Live-subscribe to the user doc so role flips (e.g. an admin granting
        // isAdmin/isReviewer) take effect without requiring a sign-out.
        roleUnsub = identityRepository.subscribeRoles(
          user.uid,
          (roles) => {
            if (!mounted || initSeqRef.current !== seq) return;
            setIsAdmin(roles.isAdmin);
            setIsReviewer(roles.isReviewer);
            setReviewCampaignIds(roles.reviewCampaignIds);
            setTrustedCampaignIds(roles.trustedCampaignIds ?? []);
            setExpertId(roles.expertId);
          },
        );
      };

      void init();
    });

    return () => {
      mounted = false;
      initSeqRef.current += 1;
      if (roleUnsub) roleUnsub();
      unsubscribe();
    };
  }, [identityRepository]);

  const value: AuthContextType = {
    currentUser,
    loading: loading || redirectLoading,
    isAdmin,
    isReviewer,
    reviewCampaignIds,
    trustedCampaignIds,
    expertId,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
